import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { useLocation } from 'react-router-dom';
import './Wallet.css';

const RECHARGE_OPTIONS = [100, 500, 1000, 2000, 5000];

const Wallet = () => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [recharging, setRecharging] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const location = useLocation();

  useEffect(() => {
    Promise.all([authService.getWalletBalance(), authService.getTransactions(), authService.getReferralInfo()])
      .then(([b, t, r]) => {
        setBalance(b.data.balance);
        setTransactions(t.data);
        setReferral(r.data);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('payment_status');
    const amt = params.get('amount');

    if (status === 'success') {
      setToast({ type: 'success', message: `${amt} coins added to wallet!` });
      // Refresh balance and transactions
      Promise.all([authService.getWalletBalance(), authService.getTransactions()])
        .then(([b, t]) => {
          setBalance(b.data.balance);
          setTransactions(t.data);
        });
    } else if (status === 'failed') {
      setToast({ type: 'error', message: 'Payment failed or cancelled' });
    }

    // Clear query params from URL
    if (status) {
      window.history.replaceState({}, '', '/wallet');
    }
  }, [location]);

  // Auto-dismiss toast after 4 seconds
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleRecharge = async (e) => {
    e.preventDefault();
  
    if (!amount || amount < 100) {
      setError('Minimum recharge is 100 coins');
      return;
    }
  
    setRecharging(true);
    setError('');
    setSuccess('');
    const amt = Number(amount);
  
    try {
      const { data: order } = await authService.createOrder({ amount: amt });
      if (order && order.short_url) {
        //window.location.href = order.short_url; // redirect
        window.open(order.short_url, '_blank')
      } else {
        setError("Failed to create payment order");
      }
    } catch {
      setError("Recharge failed. Try again.");
    } finally {
      setRecharging(false);
    }
  };

  return (
    <div className="wallet-wrapper">
      {toast && (
        <div className={`wallet-toast ${toast.type}`}>
          <span>{toast.type === 'success' ? '✓' : '✕'}</span>
          <p>{toast.message}</p>
          <button onClick={() => setToast(null)}>×</button>
        </div>
      )}
      <div className="wallet-header">
        <button className="wallet-back" onClick={() => navigate('/dashboard')}>← Back</button>
        <h2>💰 My Wallet</h2>
        <span />
      </div>

      {loading ? <div className="wallet-loading">Loading...</div> : (
        <div className="wallet-body">

          {/* Balance Card */}
          <div className="wallet-balance-card">
            <p className="wallet-balance-label">Available Balance</p>
            <h1 className="wallet-balance-amount">🪙 {balance.toFixed(0)} coins</h1>
            <p className="wallet-balance-note">100 coins per contact unlock (1 Rupee = 1 Coin)</p>
          </div>

          {/* Recharge */}
          <div className="wallet-section">
            <h3>Recharge Wallet</h3>
            <div className="wallet-quick-amounts">
              {RECHARGE_OPTIONS.map(opt => (
                <button
                  key={opt}
                  className={`wallet-quick-btn ${amount == opt ? 'active' : ''}`}
                  onClick={() => setAmount(opt)}
                >
                  ₹{opt} = {opt} coins
                </button>
              ))}
            </div>
            <form className="wallet-recharge-form" onSubmit={handleRecharge}>
              <input
                type="number"
                placeholder="Enter custom amount"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                min="100"
              />
              <button type="submit" disabled={recharging}>
                {recharging ? 'Redirecting...' : 'Recharge Now'}
              </button>
            </form>
            {success && <p className="wallet-success">{success}</p>}
            {error && <p className="wallet-error">{error}</p>}
          </div>

          {/* Referral Program */}
          {referral && (
            <div className="wallet-section">
              <h3>🎁 Referral Program</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: 12 }}>
                Share your referral code with friends. You earn 10% of their first deposit amount.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <code style={{ background: '#f0f0f0', padding: '10px 16px', borderRadius: 8, fontSize: '1.1rem', fontWeight: 700, letterSpacing: 2 }}>
                  {referral.referral_code}
                </code>
                <button
                  onClick={() => { navigator.clipboard.writeText(referral.referral_code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  style={{ padding: '8px 16px', background: copied ? '#27ae60' : '#c0392b', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  {copied ? '✓ Copied' : 'Copy Code'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 20 }}>
                <div style={{ background: '#f9f5f0', padding: 12, borderRadius: 8, flex: 1, textAlign: 'center' }}>
                  <p style={{ fontSize: '0.8rem', color: '#888' }}>Total Referrals</p>
                  <p style={{ fontSize: '1.3rem', fontWeight: 700 }}>{referral.total_referrals}</p>
                </div>
                <div style={{ background: '#f9f5f0', padding: 12, borderRadius: 8, flex: 1, textAlign: 'center' }}>
                  <p style={{ fontSize: '0.8rem', color: '#888' }}>Rewards Earned</p>
                  <p style={{ fontSize: '1.3rem', fontWeight: 700 }}>🪙 {referral.total_earned}</p>
                </div>
              </div>
            </div>
          )}

          {/* Transactions */}
          <div className="wallet-section">
            <h3>Transaction History</h3>
            {transactions.length === 0 ? (
              <div className="wallet-empty">No transactions yet</div>
            ) : (
              <div className="wallet-transactions">
                {transactions.map(t => (
                  <div key={t.id} className="wallet-txn">
                    <div className="wallet-txn-icon">{t.type === 'credit' ? '↑' : '↓'}</div>
                    <div className="wallet-txn-info">
                      <p className="wallet-txn-desc">{t.description}</p>
                      <p className="wallet-txn-date">{new Date(t.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="wallet-txn-right">
                      <p className={`wallet-txn-amount ${t.type}`}>
                        {t.type === 'credit' ? '+' : '-'}{t.amount} coins
                      </p>
                      <p className="wallet-txn-balance">Bal: {t.balance_after} coins</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default Wallet;